import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle2,
  Briefcase,
  TrendingUp,
  Users,
  Shield,
  Star,
  Quote,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { testimonials } from "../data/mock";
import * as LucideIcons from "lucide-react";

const Home = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Get data directly from mock - No API
    import("../data/mock").then((module) => {
      // 2. Convert Object to Array so .map() works
      const servicesArray = Object.values(module.services);

      const servicesWithIcons = servicesArray.map((service) => ({
        ...service,
        icon: LucideIcons[service.icon_name] || LucideIcons.Briefcase,
      }));

      setServices(servicesWithIcons);
      setLoading(false);
    });
  }, []);

  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="h-12 w-12 border-4 border-maroon-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 lg:px-24 overflow-hidden bg-gray-50">
        {/* Advanced Background Elements */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
            alt="Business Growth"
            className="w-full h-full object-cover opacity-5"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-white via-rose-50/50 to-white opacity-90"></div>
          {/* Subtle decorative blurred circles */}
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[500px] h-[500px] bg-maroon-600/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[400px] h-[400px] bg-rose-400/10 rounded-full blur-[80px]"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content Area */}
            <motion.div
              className="space-y-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* FIX 1: Adaptive mobile pill layout */}
              <motion.div variants={itemVariants} className="inline-block">
                <span className="inline-flex items-center justify-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-white/80 border border-maroon-100 text-maroon-700 rounded-full text-xs sm:text-sm md:text-base font-semibold shadow-sm backdrop-blur-md">
                  <Sparkles className="w-4 h-4 text-maroon-500 shrink-0" />
                  <span>Your Complete Business Solutions Partner</span>
                </span>
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-[1.1] tracking-tight"
              >
                Elevate Your Business to
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-maroon-600 to-rose-500">
                  {" "}
                  New Heights
                </span>
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl"
              >
                Focus on what you do best while we strengthen your business from
                every angle—from digital innovation to financial excellence.
              </motion.p>

              <motion.div
                variants={itemVariants}
                className="flex flex-wrap gap-4 pt-4"
              >
                {/* FIX 2: Standard anchor tag with mailto link */}
                <a href="mailto:info@maroons.ai" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-maroon-600 hover:bg-maroon-700 hover:shadow-lg hover:shadow-maroon-600/20 text-white px-8 py-6 text-lg rounded-xl transition-all duration-300 group"
                  >
                    Get Started Today
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>

                <Link to="/services" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-gray-200 text-gray-700 hover:border-maroon-600 hover:text-maroon-600 hover:bg-maroon-50 px-8 py-6 text-lg rounded-xl transition-all duration-300"
                  >
                    Explore Services
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Content Area (Image & Stats) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block relative"
            >
              {/* Glowing backdrop ring */}
              <div className="absolute -inset-4 bg-gradient-to-r from-maroon-500 to-rose-400 rounded-[2.5rem] opacity-20 blur-xl"></div>

              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20 group">
                <img
                  src="https://res.cloudinary.com/dkoljvhc9/image/upload/v1777485843/photo-1600880292203-757bb62b4baf_1_tba9jw.jpg"
                  alt="Business Team Success"
                  className="w-full h-[550px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent"></div>

                {/* Advanced Glassmorphism Stats Card */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-8">
                    {[
                      {
                        icon: Briefcase,
                        label: "Business Focus",
                        value: "500+",
                      },
                      {
                        icon: TrendingUp,
                        label: "Growth Driven",
                        value: "250%",
                      },
                      { icon: Users, label: "Happy Clients", value: "1000+" },
                      { icon: Shield, label: "Trust Rating", value: "100%" },
                    ].map((stat, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="p-2.5 bg-white/20 rounded-lg shrink-0">
                          <stat.icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-white leading-none mb-1">
                            {stat.value}
                          </div>
                          <div className="text-sm font-medium text-gray-300">
                            {stat.label}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- WHY CHOOSE US --- */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Why <span className="text-maroon-600">Maroons</span>?
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We combine expertise, innovation, and dedication to transform
                your business challenges into opportunities.
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1757405939046-7658d7426026"
                alt="Professional Collaboration"
                className="w-full h-[300px] object-cover"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: CheckCircle2, title: "Expert Team" },
              { icon: TrendingUp, title: "Results-Driven" },
              { icon: Shield, title: "Trust" },
              { icon: Users, title: "Client-Centric" },
            ].map((item, idx) => (
              <Card
                key={idx}
                className="border-none shadow-md hover:shadow-lg transition-all duration-300"
              >
                <CardContent className="p-6 text-center">
                  <item.icon className="h-10 w-10 text-maroon-600 mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900">{item.title}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* --- ADVANCED SERVICES SECTION --- */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden">
        {/* Background Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-maroon-50 rounded-full blur-[120px] opacity-60 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* 1. Introductory Image & Text Block */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl order-2 lg:order-1 group">
              {/* Dark overlay that fades slightly on hover */}
              <div className="absolute inset-0 bg-maroon-900/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
              <img
                src="https://images.unsplash.com/photo-1757405944970-dfca42373629"
                alt="Professional Business Meeting"
                className="w-full h-[450px] object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              {/* Bottom gradient to make text readable */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-20"></div>

              <div className="absolute bottom-8 left-8 right-8 text-white z-30 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                <div className="w-12 h-1 bg-maroon-500 mb-4 rounded-full"></div>
                <h3 className="text-3xl font-black mb-2 tracking-tight">
                  Professional Excellence
                </h3>
                <p className="text-white/80 font-medium text-lg leading-relaxed">
                  Delivering world-class solutions for your business success.
                </p>
              </div>
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 text-maroon-700 font-bold uppercase tracking-widest text-sm">
                <span className="h-px w-8 bg-maroon-700"></span>
                Our Capabilities
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.1]">
                Comprehensive <br />
                <span className="text-maroon-700">Business Solutions</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                From strategic planning to technological execution, we cover all
                aspects of your enterprise needs under one roof. Our
                multidisciplinary approach ensures cohesive growth.
              </p>
            </div>
          </div>

          {/* 2. Advanced Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -8 }}
                className="group relative bg-white rounded-3xl border border-gray-100 p-8 shadow-sm hover:shadow-2xl hover:shadow-maroon-900/20 transition-all duration-500 overflow-hidden flex flex-col h-full z-10 cursor-pointer"
              >
                {/* Hover Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-maroon-700 to-maroon-900 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative z-10 flex flex-col flex-grow">
                  {/* Icon Wrapper */}
                  <div className="h-14 w-14 rounded-2xl bg-maroon-50 flex items-center justify-center mb-8 group-hover:bg-white/10 transition-colors duration-500 border border-transparent group-hover:border-white/20">
                    {service.icon && (
                      <service.icon className="h-6 w-6 text-maroon-700 group-hover:text-white transition-colors duration-500" />
                    )}
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-white transition-colors duration-500">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-8 line-clamp-3 leading-relaxed flex-grow group-hover:text-maroon-100 transition-colors duration-500">
                    {service.heroDescription || service.tagline}
                  </p>

                  {/* Footer Link */}
                  <div className="mt-auto">
                    <Link
                      to={`/services#${service.id}`}
                      className="inline-flex items-center text-maroon-700 font-bold text-sm group-hover:text-white transition-colors duration-500"
                    >
                      Explore Service
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-2 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* 3. Section Footer / CTA */}
          <div className="mt-16 text-center relative z-10">
            <Link to="/services" onClick={() => window.scrollTo(0, 0)}>
              <Button
                size="lg"
                className="bg-gray-900 hover:bg-maroon-700 text-white px-10 py-6 rounded-xl font-bold text-lg shadow-xl hover:shadow-maroon-900/20 transition-all transform hover:-translate-y-1"
              >
                View Complete Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS SECTION --- */}
      {/* --- TESTIMONIALS SECTION --- */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gray-50 relative overflow-hidden">
        {/* Decorative background element for premium feel */}
        <div className="absolute top-0 left-0 w-full h-[400px] bg-maroon-900 rounded-b-[4rem] md:rounded-b-[8rem] z-0"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-maroon-100 text-sm font-bold uppercase tracking-widest mb-6 backdrop-blur-md"
            >
              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
              Verified Success Stories
            </motion.span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
              Trusted by <span className="text-maroon-300">Visionaries</span>
            </h2>
            <p className="text-lg text-maroon-50 max-w-2xl mx-auto opacity-90 leading-relaxed">
              Hear directly from founders and directors who transformed their
              businesses with our tailored consulting, financial, and technology
              solutions.
            </p>
          </div>

          {/* Masonry-style Grid for longer content */}
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="h-full"
              >
                <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-500 h-full flex flex-col bg-white rounded-3xl overflow-hidden group">
                  <CardContent className="p-8 md:p-10 flex flex-col h-full relative">
                    {/* Big Quote Watermark */}
                    <Quote className="absolute top-8 right-8 h-24 w-24 text-gray-50 opacity-60 rotate-12 group-hover:scale-110 group-hover:rotate-0 transition-transform duration-500" />

                    {/* Header: Photo, Name, Company, Stars */}
                    <div className="flex items-center space-x-5 mb-8 relative z-10">
                      <div className="relative shrink-0">
                        <img
                          src={testimonial.photo}
                          alt={testimonial.name}
                          className="h-16 w-16 md:h-20 md:w-20 rounded-full object-cover ring-4 ring-maroon-50"
                        />
                        {/* Verified Badge */}
                        <div
                          className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 border-2 border-white shadow-sm"
                          title="Verified Client"
                        >
                          <CheckCircle2 className="h-3 w-3 text-white" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-black text-gray-900 text-lg md:text-xl leading-tight mb-1">
                          {testimonial.name}
                        </h4>
                        <p className="text-sm text-gray-500 font-medium mb-2">
                          {testimonial.position} ·{" "}
                          <span className="text-maroon-700 font-bold uppercase tracking-wide text-xs">
                            {testimonial.company}
                          </span>
                        </p>
                        {/* 5-Star Rating */}
                        <div className="flex gap-1">
                          {[...Array(testimonial.rating || 5)].map((_, i) => (
                            <Star
                              key={i}
                              className="h-4 w-4 text-yellow-400 fill-yellow-400"
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Body: Long Quote Content */}
                    <div className="relative flex-grow z-10">
                      <p className="text-gray-700 leading-relaxed font-medium text-[15px] italic">
                        {testimonial.content}
                      </p>
                    </div>

                    {/* Footer: Dynamic Tags Rendering */}
                    {testimonial.tags && testimonial.tags.length > 0 && (
                      <div className="pt-8 mt-auto relative z-10">
                        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-6">
                          {testimonial.tags.map((tag, tIdx) => (
                            <span
                              key={tIdx}
                              className="px-3 py-1.5 bg-maroon-50 border border-maroon-100 text-maroon-700 rounded-lg text-xs font-bold uppercase tracking-wider"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-r from-maroon-700 to-maroon-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Scale Your Business?
          </h2>
          <p className="text-maroon-100 mb-10 text-xl max-w-2xl mx-auto">
            Partner with us today for strategic consulting and innovative
            technology solutions.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-maroon-700 hover:bg-gray-100 px-12 py-7 text-lg rounded-xl font-bold shadow-2xl transition-transform hover:scale-105 active:scale-95"
            >
              Schedule Consultation Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
